import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Controller, useFormContext } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { tv } from 'tailwind-variants';
import useSettings from '@/hooks/useSettings';
import AlertIcon from '@/icons/AlertIcon';
import './TextFieldPhoneNumber.css';

const textfield = tv({
  base: `
  border
  block
  duration-150
  ease-in-out
  focus:ring-1
  outline-none
  rounded-md
  shadow-sm
  text-white
  transition
  `,
  variants: {
    color: {
      primary: 'focus:ring-white focus:border-white',
      error: 'border-red-500 focus:ring-red-500 focus:border-red-500'
    },
    size: {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-6 py-4 text-lg'
    }
  },
  defaultVariants: {
    color: 'primary',
    size: 'md'
  }
});

type TextFieldPhoneNumberProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'color'
> & {
  name: string;
  label: string;
  color?: 'primary' | 'error';
  size?: 'sm' | 'md' | 'lg';
  normalize?: boolean;
  appendPlaceholder?: string;
};

const TextFieldPhoneNumber: React.FC<TextFieldPhoneNumberProps> = ({
  color = 'primary',
  name,
  size = 'md',
  label,
  className,
  normalize,
  ...props
}) => {
  const { text, form } = useSettings();
  const { control, setValue } = useFormContext();
  const [searchParams] = useSearchParams();

  const param = searchParams.get(name);

  useEffect(() => {
    if (param) {
      setTimeout(() => {
        setValue(name, param);
      }, 100);
    }
  }, [name, param, setValue]);

  // const handlePhoneChange = (phone: string, country: any) => {
  //   const phoneWithoutCountryCode = phone.replace(`${country.dialCode}`, '');

  //   if (phoneWithoutCountryCode && phoneWithoutCountryCode.length === 11) {
  //     setValue(
  //       name,
  //       `(${phoneWithoutCountryCode.slice(0, 2)}) ${phoneWithoutCountryCode.slice(2, 7)}-${phoneWithoutCountryCode.slice(7)}`
  //     );
  //   } else {
  //     setValue(name, phoneWithoutCountryCode);
  //   }
  // };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className={`flex flex-col w-full items-start gap-1 ${className}`}>
          <label
            style={{ color: text?.color?.secondary }}
            htmlFor={name}
            className="text-slate-500 text-xs"
          >
            {label}
          </label>
          <div className="relative w-full">
            <PhoneInput
              placeholder="Preencha seu celular"
              masks={{ br: '(..) .....-....' }}
              enableSearch
              inputProps={{
                name: name,
                id: name
              }}
              country={'br'}
              value={field.value}
              enableAreaCodeStretch={false}
              // onChange={(phone: string, country: any) => handlePhoneChange(phone, country)}
              inputClass={textfield({
                color: error ? 'error' : color,
                size,
                className: props['disabled'] ? 'bg-gray-100 cursor-not-allowed' : ''
              })}
              onChange={(value) => {
                setValue(name, value);
              }}
              inputStyle={{
                backgroundColor: form?.background.color,
                borderColor: '#919EAB33',
                width: '100%',
                color: text?.color.primary
              }}
              searchStyle={{
                backgroundColor: form?.background.color,
                color: text?.color.primary
              }}
              dropdownStyle={{
                backgroundColor: form?.background.color,
                color: text?.color.primary
              }}
              containerStyle={{
                backgroundColor: form?.background.color,
                color: text?.color.primary
              }}
              buttonStyle={{ backgroundColor: form?.background.color, borderColor: '#919EAB33' }}
              searchPlaceholder="Buscar país"
            />
            {error && (
              <AlertIcon className="absolute transform -translate-y-1/2 right-2 top-1/2 w-5 h-5 text-red-500" />
            )}
            {props?.appendPlaceholder && (
              <span
                style={{
                  color: 'rgba(56, 202, 79, 1)'
                }}
                className="absolute top-2 right-2 "
              >
                {props.appendPlaceholder}
              </span>
            )}
          </div>
          {error && <span className="text-red-500 text-xs">{error.message}</span>}
        </div>
      )}
    />
  );
};

export default TextFieldPhoneNumber;
